import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Store, Package, ShoppingBag, TrendingUp, CheckCircle, Shield, Layout } from 'lucide-react';
import COLORS from '../utils/colors';

const BrandOnboarding = ({ isOpen, onClose, username }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Welcome to Your Fashion Brand! 🎉",
      icon: <Sparkles size={48} />,
      content: (
        <div className="text-center space-y-4">
          <p className="text-lg text-gray-700">
            Congratulations! Your independent online store is now <span className="font-bold text-green-600">LIVE</span>
          </p>
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 border-2 border-orange-300 rounded-lg p-6">
            <p className="text-sm text-gray-600 mb-2">Your store is live at:</p>
            <p className="text-lg sm:text-2xl font-bold text-orange-600 break-all">
              https://{username}.vasstr.com
            </p>
            <p className="text-sm text-gray-500 mt-3">
              Share this URL with your customers!
            </p>
          </div>
          <p className="text-base text-gray-600">
            You're not selling on a marketplace - this is <span className="font-bold">YOUR branded website</span> where you control everything!
          </p>
        </div>
      )
    },
    {
      title: "Your Brand Dashboard",
      icon: <Store size={48} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            This is your <span className="font-bold">Brand Dashboard</span> - your control center for managing your store:
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center flex-shrink-0 mt-0.5">1</div>
              <div>
                <p className="font-semibold text-gray-800">Store Stats</p>
                <p className="text-sm text-gray-600">Track total designs, store status, and inventory at a glance</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center flex-shrink-0 mt-0.5">2</div>
              <div>
                <p className="font-semibold text-gray-800">Store Catalog</p>
                <p className="text-sm text-gray-600">View and manage all your designs in one place</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center flex-shrink-0 mt-0.5">3</div>
              <div>
                <p className="font-semibold text-gray-800">Quick Actions</p>
                <p className="text-sm text-gray-600">Quickly add designs or view your live store</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Add Your First Design",
      icon: <Package size={48} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Ready to showcase your designs? Here's how to add them:
          </p>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center flex-shrink-0">1</div>
              <div>
                <p className="font-semibold text-gray-800">Click "Add Design" or "Add New Design"</p>
                <p className="text-sm text-gray-600">Found in the Quick Actions banner or navigation menu</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center flex-shrink-0">2</div>
              <div>
                <p className="font-semibold text-gray-800">Upload Photos & Add Details</p>
                <p className="text-sm text-gray-600">Add up to 6 photos, name, price, description, and customization options</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center flex-shrink-0">3</div>
              <div>
                <p className="font-semibold text-gray-800">Click "Add to My Store Catalog"</p>
                <p className="text-sm text-gray-600">Your design goes live instantly on your store!</p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">💡 Pro Tip:</span> Add at least 5-10 designs to give customers a good selection!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Key Features",
      icon: <ShoppingBag size={48} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Here's what you can do with your brand dashboard:
          </p>
          <div className="grid grid-cols-1 gap-3">
            <div className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-orange-300 transition">
              <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={24} />
              <div>
                <p className="font-semibold text-gray-800">Manage Catalog</p>
                <p className="text-sm text-gray-600">Add, edit, delete designs. Toggle stock status anytime.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-orange-300 transition">
              <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={24} />
              <div>
                <p className="font-semibold text-gray-800">Track Orders</p>
                <p className="text-sm text-gray-600">View customer orders from YOUR store (in Orders section)</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-orange-300 transition">
              <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={24} />
              <div>
                <p className="font-semibold text-gray-800">Custom Requests</p>
                <p className="text-sm text-gray-600">Handle custom design requests from your customers</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-orange-300 transition">
              <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={24} />
              <div>
                <p className="font-semibold text-gray-800">Create Coupons</p>
                <p className="text-sm text-gray-600">Offer discounts to YOUR customers to boost sales</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-orange-300 transition">
              <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={24} />
              <div>
                <p className="font-semibold text-gray-800">Brand Settings</p>
                <p className="text-sm text-gray-600">Customize your profile and brand information</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Complete Designer KYC",
      icon: <Shield size={48} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            To receive payouts from your sales, you need to complete the KYC verification process:
          </p>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center flex-shrink-0">1</div>
              <div>
                <p className="font-semibold text-gray-800">Navigate to Designer KYC</p>
                <p className="text-sm text-gray-600">Find it in the sidebar menu under your profile section</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center flex-shrink-0">2</div>
              <div>
                <p className="font-semibold text-gray-800">Submit Required Documents</p>
                <p className="text-sm text-gray-600">PAN card, bank account details, and IFSC code</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center flex-shrink-0">3</div>
              <div>
                <p className="font-semibold text-gray-800">Get Verified</p>
                <p className="text-sm text-gray-600">Once approved, you can start receiving payouts directly to your account</p>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <span className="font-semibold">⚡ Important:</span> KYC verification is required to receive payments from customer orders!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Choose Your Template",
      icon: <Layout size={48} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Customize how your store looks by selecting from 20+ professional templates:
          </p>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center flex-shrink-0">1</div>
              <div>
                <p className="font-semibold text-gray-800">Browse Templates</p>
                <p className="text-sm text-gray-600">Go to Templates section and explore 20+ designs in different categories</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center flex-shrink-0">2</div>
              <div>
                <p className="font-semibold text-gray-800">Select Your Favorite</p>
                <p className="text-sm text-gray-600">Choose a template that matches your brand style (Fashion, Modern, or Minimal)</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center flex-shrink-0">3</div>
              <div>
                <p className="font-semibold text-gray-800">Customize It</p>
                <p className="text-sm text-gray-600">Add your hero images, banners, categories, and brand content</p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">💡 Pro Tip:</span> Each template comes with 9 complete pages including shop, cart, checkout, and more!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "You're All Set! 🚀",
      icon: <TrendingUp size={48} />,
      content: (
        <div className="text-center space-y-4">
          <p className="text-lg text-gray-700">
            Your independent fashion brand is ready to grow!
          </p>
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-6">
            <p className="text-lg sm:text-xl font-bold text-green-700 mb-3">Next Steps:</p>
            <div className="space-y-2 text-left">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">1</div>
                <p className="text-gray-700">Add your first design to your catalog</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">2</div>
                <p className="text-gray-700">Complete your Designer KYC verification</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">3</div>
                <p className="text-gray-700">Choose and customize your template</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">4</div>
                <p className="text-gray-700">Share your store URL with customers</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">5</div>
                <p className="text-gray-700">Start receiving orders on YOUR brand!</p>
              </div>
            </div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Need Help?</span> Click the help icons (?) throughout the dashboard for guidance anytime.
            </p>
          </div>
        </div>
      )
    }
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Mark onboarding as completed in localStorage
      localStorage.setItem('onboarding_completed', 'true');
      onClose();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipOnboarding = () => {
    localStorage.setItem('onboarding_completed', 'true');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={skipOnboarding}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white rounded-2xl shadow-2xl max-w-full sm:max-w-2xl w-full mx-3 sm:mx-0 max-h-[90vh] overflow-y-auto"
        >
          {/* Close button */}
          <button
            onClick={skipOnboarding}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition z-10"
          >
            <X size={24} />
          </button>

          {/* Progress indicator */}
          <div className="bg-gray-100 h-2 rounded-t-2xl overflow-hidden">
            <motion.div
              className="h-full"
              style={{ backgroundColor: COLORS.AUTUMN }}
              initial={{ width: '0%' }}
              animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          {/* Content */}
          <div className="p-4 sm:p-8">
            {/* Step indicator */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 mb-4" style={{ color: COLORS.AUTUMN }}>
                {steps[currentStep].icon}
              </div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                {steps[currentStep].title}
              </h2>
              <p className="text-sm text-gray-500">
                Step {currentStep + 1} of {steps.length}
              </p>
            </div>

            {/* Step content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {steps[currentStep].content}
              </motion.div>
            </AnimatePresence>

            {/* Navigation buttons */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={prevStep}
                disabled={currentStep === 0}
                className={`px-6 py-2 rounded-lg font-medium transition ${
                  currentStep === 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Previous
              </button>

              <div className="flex gap-2">
                {steps.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition ${
                      index === currentStep ? 'bg-orange-500 w-8' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={nextStep}
                className="px-6 py-2 rounded-lg font-medium text-white transition hover:opacity-90"
                style={{ backgroundColor: COLORS.AUTUMN }}
              >
                {currentStep === steps.length - 1 ? "Get Started!" : "Next"}
              </button>
            </div>

            {/* Skip link */}
            {currentStep < steps.length - 1 && (
              <div className="text-center mt-4">
                <button
                  onClick={skipOnboarding}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Skip tutorial
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default BrandOnboarding;
